/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import { useMachine } from '@xstate/react/lib';
import { FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'next-i18next';
import React, { ReactNode, useContext, useState } from 'react';
import { StateValue } from 'xstate';

import { FormValues } from '@toucan/services/request-client';
import { sendVerification, signUp } from '@toucan/services/requests/user';
import {
    SignUpMachineAction,
    SignUpMachineState,
    signUpMachine,
} from '@toucan/services/state-machines/sign-up.machine';

import { SignUpForm } from '@toucan/components/views/sign-up-funnel/components/sign-up-form';

import { LoginForm } from '@toucan/components/organisms/login-form';
import { RequestPasswordReset } from '@toucan/components/organisms/request-password-reset';

import {
    ApplicationContext,
    ModalSize,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { Link, LinkColor } from '@toucan/client/shared/components/link';
import { SignUpConfirmationPending } from '@toucan/client/shared/components/sign-up-confirmation-pending';
import { useCsrf } from '@toucan/client/shared/hooks/use-csrf';

export const SignUpFunnel = () => {
    const { csrfToken } = useCsrf();
    const { setModalVisible, setModalContent, setModalSize } =
        useContext(ApplicationContext);
    const { t } = useTranslation();
    const [current, send] = useMachine(signUpMachine);
    const [email, setEmail] = useState<string>();
    const { activeMarket } = useContext(MarketContext);

    function handleOpenLogin() {
        setModalSize(ModalSize.SMALL);
        setModalContent(
            <LoginForm onLogInSuccess={() => setModalVisible(false)} />
        );
        setModalVisible(true);
    }

    function handleOpenResetPassword(email: string) {
        setModalContent(<RequestPasswordReset initialEmail={email} />);
        setModalSize(ModalSize.SMALL);
        setModalVisible(true);
    }

    /**
     * Add the log in button to the duplicate email address validation message.
     *
     * @param errorMessage the duplicate email address validation message
     * @param email the email address in the form, used to send to password
     * reset if that option is chosen
     */
    function addLoginButtonToEmailValidationError(
        errorMessage: string,
        email: string
    ): ReactNode | string {
        if (errorMessage.includes('{login=log in}')) {
            errorMessage =
                "It looks like you've already joined MPB - welcome back. Please <0>log in</0> or <1>reset your password</1>.";
        }
        return (
            <Trans
                components={[
                    <Link
                        key={0}
                        onClick={handleOpenLogin}
                        linkColor={LinkColor.LIGHT_ALT}
                    >
                        $1
                    </Link>,
                    <Link
                        key={1}
                        linkColor={LinkColor.LIGHT_ALT}
                        onClick={() => handleOpenResetPassword(email)}
                    >
                        $1
                    </Link>,
                ]}
            >
                {t(errorMessage)}
            </Trans>
        );
    }

    /**
     * Handle the sign up form submit. Calls the sign up service and transitions
     * the state machine on success or displays any errors.
     *
     * @param values {FormValues} the submitted form values
     * @param formActions {FormikHelpers<FormValues>} actions which can be performed on the formik object state
     */
    async function handleSignUp(
        values: FormValues,
        formActions: FormikHelpers<FormValues>
    ) {
        try {
            // disable form submission
            formActions.setSubmitting(true);

            // call our sign up service
            const response = await signUp(
                {
                    ...values,
                    newsletterMarket: activeMarket.code,
                },
                activeMarket.code,
                csrfToken
            );
            setEmail(response.data.providedEmail);

            // on success set our response in the state machine context then transition
            send(SignUpMachineAction.SUBMIT);
        } catch (error: any) {
            // re enable form submission
            formActions.setSubmitting(false);

            console.error(error);
            if (error?.validationErrors) {
                if (error.validationErrors.emailAddress) {
                    error.validationErrors.emailAddress =
                        addLoginButtonToEmailValidationError(
                            error.validationErrors.emailAddress,
                            values.emailAddress as string
                        );
                }
                formActions.setErrors(error.validationErrors);
            }
        }
    }

    /**
     * Handle resending the account verification email.
     *
     * @param email {string} the account email
     */
    async function handleResendEmail(email: string) {
        try {
            await sendVerification(email, activeMarket.code);
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Given a state machine state choose the corresponding view.
     *
     * @param state {StateValue} the state machine state
     */
    function view(state: StateValue) {
        switch (state) {
            case SignUpMachineState.NEARLY_THERE:
                return (
                    <SignUpConfirmationPending
                        email={email || ''}
                        onResendEmail={handleResendEmail}
                    />
                );

            case SignUpMachineState.FORM:
            default:
                return (
                    <SignUpForm
                        onSignUp={handleSignUp}
                        onOpenLogin={handleOpenLogin}
                    />
                );
        }
    }

    return view(current.value);
};
